/**
 * CarouselThumb Component
 *
 * Shared full-width square media carousel used by both SidebarPostPopover
 * and CalendarPostPopover. Supports single images, videos, and multi-slide
 * carousels with auto-rotation and manual navigation.
 */

import { useState, useEffect, useCallback } from 'react';
import { PostMedia } from '@/services/posts';
import { Icon } from '@/components/ui/Icon';
import { RetryImage, PlaceholderImage } from '@/components/ui';
import { MediaTypeBadge } from '@/components/ui/MediaTypeBadge';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';

export interface CarouselThumbProps {
  media: PostMedia[];
  mediaCount: number;
  firstMedia: PostMedia | undefined;
}

export const CarouselThumb = ({ media, mediaCount, firstMedia }: CarouselThumbProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [fading, setFading] = useState(false);
  const [paused, setPaused] = useState(false);
  const isCarousel = mediaCount > 1;

  const goTo = useCallback((index: number) => {
    setFading(true);
    setTimeout(() => {
      setActiveIndex(index);
      setFading(false);
    }, 200);
  }, []);

  const advance = useCallback(() => {
    goTo((activeIndex + 1) % mediaCount);
  }, [activeIndex, mediaCount, goTo]);

  const goBack = useCallback(() => {
    goTo((activeIndex - 1 + mediaCount) % mediaCount);
  }, [activeIndex, mediaCount, goTo]);

  const current = media[activeIndex] || firstMedia;
  const hasCurrent = current && (current.thumbnailUrl || current.originalUrl);
  const isVideo = current?.type === 'VIDEO';

  useEffect(() => {
    if (!isCarousel || paused || isVideo) return;
    const interval = setInterval(advance, 3000);
    return () => clearInterval(interval);
  }, [isCarousel, paused, isVideo, advance]);

  return (
    <div
      className="aspect-square bg-gray-100 relative overflow-hidden group/carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {hasCurrent ? (
        current.type === 'VIDEO' && current.originalUrl ? (
          <video
            src={current.originalUrl}
            muted
            autoPlay
            loop
            playsInline
            className={cn(
              'w-full h-full object-cover transition-opacity duration-200',
              fading ? 'opacity-0' : 'opacity-100'
            )}
          />
        ) : current.type === 'VIDEO' && current.thumbnailUrl ? (
          <img
            src={current.thumbnailUrl}
            alt=""
            className={cn(
              'w-full h-full object-cover transition-opacity duration-200',
              fading ? 'opacity-0' : 'opacity-100'
            )}
          />
        ) : (
          <RetryImage
            src={current.thumbnailUrl || current.originalUrl || undefined}
            alt=""
            className={cn(
              'w-full h-full object-cover transition-opacity duration-200',
              fading ? 'opacity-0' : 'opacity-100'
            )}
          />
        )
      ) : (
        <PlaceholderImage />
      )}

      {/* Prev/Next arrows on hover */}
      {isCarousel && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); goBack(); }}
            className="absolute left-1 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/50 text-white flex items-center justify-center opacity-0 group-hover/carousel:opacity-100 transition-opacity hover:bg-black/70"
            aria-label="Previous"
          >
            <Icon name={ICONS.ARROW_LEFT} size="xs" ariaHidden />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); advance(); }}
            className="absolute right-1 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/50 text-white flex items-center justify-center opacity-0 group-hover/carousel:opacity-100 transition-opacity hover:bg-black/70"
            aria-label="Next"
          >
            <Icon name={ICONS.ARROW_RIGHT} size="xs" ariaHidden />
          </button>
        </>
      )}

      {/* Carousel dots - clickable with backdrop */}
      {isCarousel && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1.5 px-2 py-1 bg-black/40 backdrop-blur-sm rounded-full">
          {media.map((_, i) => (
            <button
              key={i}
              onClick={(e) => { e.stopPropagation(); goTo(i); }}
              className={cn(
                'w-2 h-2 rounded-full transition-all',
                i === activeIndex ? 'bg-white scale-110' : 'bg-white/50 hover:bg-white/75'
              )}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>
      )}

      {/* Media type badge */}
      {mediaCount > 0 && (
        <div className="absolute top-2 right-2">
          <MediaTypeBadge
            mediaType={firstMedia?.type || 'IMAGE'}
            mediaCount={mediaCount}
            variant="inline"
          />
        </div>
      )}
    </div>
  );
};
